import { Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export type AppLanguage = 'en' | 'ar';

export const SUPPORTED_LANGUAGES: { code: AppLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' }
];

const LANGUAGE_STORAGE_KEY = 'appLanguage';
const DEFAULT_LANGUAGE: AppLanguage = 'en';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguage$ = new BehaviorSubject<AppLanguage>(DEFAULT_LANGUAGE);

  readonly language$ = this.currentLanguage$.asObservable();

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Wire up ngx-translate on app start. English stays the default language so
   * any key missing from ar.json falls back to its English text instead of
   * rendering the raw key.
   */
  initialize(): void {
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);
    this.use(this.getStoredLanguage());
  }

  get current(): AppLanguage {
    return this.currentLanguage$.value;
  }

  use(language: AppLanguage): void {
    const lang = this.isSupported(language) ? language : DEFAULT_LANGUAGE;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    this.translate.use(lang);
    this.applyDocumentDirection(lang);
    this.currentLanguage$.next(lang);
  }

  /**
   * Switches language and reloads. A reload is required rather than just
   * calling use(): Angular CDK's Directionality reads <html dir> once when it
   * is constructed and never emits a change, so Material components (sidenav
   * side, menu/tooltip/overlay positioning) would keep the previous direction.
   * Reloading also re-initialises Google Maps and the chart widgets cleanly.
   */
  switchTo(language: AppLanguage): void {
    if (!this.isSupported(language) || language === this.current) return;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    this.document.defaultView?.location.reload();
  }

  isRtl(language: AppLanguage = this.current): boolean {
    return language === 'ar';
  }

  private getStoredLanguage(): AppLanguage {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as AppLanguage | null;
    return stored && this.isSupported(stored) ? stored : DEFAULT_LANGUAGE;
  }

  private isSupported(language: string): language is AppLanguage {
    return SUPPORTED_LANGUAGES.some((item) => item.code === language);
  }

  /**
   * Sets lang/dir on <html> so global [dir='rtl'] styles and native browser
   * behaviour (scrollbars, text alignment) follow the selected language.
   */
  private applyDocumentDirection(language: AppLanguage): void {
    const root = this.document.documentElement;
    root.setAttribute('lang', language);
    root.setAttribute('dir', this.isRtl(language) ? 'rtl' : 'ltr');
  }
}
